import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  selectCriteria,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { setAllowMessageSubmit } from '@verantos-dev/app-state-research-assistant';
import { getCohortBuilderPath } from '@verantos-dev/core-constants';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useAVAutoSavePipeline } from './useAVAutoSavePipeline';
import { useNavigateAfterRevert } from './useNavigateAfterRevert';
import { useAVEditData } from '../../containers/v0/cohort-builder-page-tabs/AVEditPage/useAVEditData';
import { useCohortBuilderViewMode } from '../../containers/v0/cohort-builder-sections/CriteriaSectionV2/hooks/useCohortBuilderViewMode';
import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortDefinitionLifecycle } from '../../hooks/initialization/useCohortDefinitionLifecycle';
import { useSocketReduxSync } from '../../hooks/initialization/useSocketReduxSync';
import { useCohortBuilderResearchAssistant } from '../../hooks/useCohortBuilderResearchAssistant';
import { isCriterionSavedToLibrary } from '../../utils/avLibraryUtils';

/**
 * Orchestrates all side-effects for the standalone AV edit page:
 * lifecycle hydration, socket sync, RA integration, data resolution,
 * auto-save pipeline, and drill-down navigation.
 */
export const useAnalyticalVariableEditPage = () => {
  const dispatch = useDispatch();
  const { projectId, cohortId, cohortVersionId, analyticalVariableId } = useParams();
  const navigate = useNavigate();
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const criteria = useCohortDefinitionStore(selectCriteria);

  const isSavedToLibrary = isCriterionSavedToLibrary(criteria, analyticalVariableId ?? '');

  // Lifecycle & socket setup
  const socketCallbacks = useSocketReduxSync(projectId, cohortId);
  useCohortDefinitionLifecycle({
    projectId: projectId ?? '',
    cohortId: cohortId ?? '',
    cohortVersionId: cohortVersionId ?? '',
    enabled: true,
    socketCallbacks,
  });

  useCohortBuilderResearchAssistant();

  useNavigateAfterRevert({ projectId, cohortId, cohortVersionId });

  const { viewMode } = useCohortBuilderViewMode();

  const { activeDialog } = useCohortBuilderModalContext();
  const isSaveDialogOpen = activeDialog === CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION;
  const isRevertDialogOpen =
    activeDialog === CohortBuilderActionDialogType.REVERT_COHORT_DEFINITION;

  // Update message submission permissions based on cohort status
  useEffect(() => {
    const allowMessageSubmit =
      !!latestStatus &&
      ![CohortDefinitionStatus.FINAL, CohortDefinitionStatus.PROCESSING].includes(latestStatus);
    setAllowMessageSubmit(allowMessageSubmit);
  }, [latestStatus]);

  // AV data & auto-save
  const {
    analyticalVariable,
    conceptGroups,
    isConceptGroupsLoading,
    cohortOwner,
    cohortLastModified,
  } = useAVEditData(analyticalVariableId ?? '');

  const handleAnalyticalVariableChange = useAVAutoSavePipeline({
    analyticalVariable,
    cohortId,
    conceptGroups,
  });

  const handleDrillDownClick = useCallback(
    (avId: string) => {
      const cohortBuilderPath = getCohortBuilderPath(
        projectId ?? '',
        cohortId ?? '',
        cohortVersionId ?? ''
      );
      navigate(`${cohortBuilderPath}?pageTab=definition&eventType=AV&analyticalVariableId=${avId}`);
    },
    [navigate, projectId, cohortId, cohortVersionId]
  );

  return {
    analyticalVariable,
    conceptGroups,
    isConceptGroupsLoading,
    cohortOwner,
    cohortLastModified,
    handleAnalyticalVariableChange,
    handleDrillDownClick,
    isEditable: !viewMode,
    isSavedToLibrary,
    isSaveDialogOpen,
    isRevertDialogOpen,
  };
};
